import type { PannelProps } from '@/app/[locale]/(auth)/signup/(funnel)/PannelType';
import styles from '@/app/[locale]/(auth)/signup/(funnel)/RegisterComp.module.scss';
import AuthCheckbox, { AllAuthCheckbox } from '@/@shared/components/forms/Checkbox/AuthCheckbox';
import { useRovingTabindex } from '@/@shared/hooks/useRovingTabindex';
import SubmitBtn from '@/legacy/components/btn/SubmitBtn';
import { LOGIN_PATHNAME } from '@/legacy/constants/pathname';
import type { SignUpType } from '@/model/zod/authZod';
import Link from 'next/link';
import type React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTransAuth } from '@/@shared/hooks/useTranslations';

const RegisterAgreements = ({ locale, onNext }: PannelProps) => {
	const t = useTransAuth('signup.agreements');

	const {
		register,
		setValue,
		getValues,
		formState: { errors },
	} = useFormContext<SignUpType>();

	const [isAllChecked, setIsAllChecked] = useState(false);

	const checkboxItems = [
		{
			key: 'registerAgreements.stepin',
			label: t('service'),
			viewLink:
				locale === 'ko'
					? 'https://help.stepin.ai/ccc6002f-4a17-4bd6-99a7-7686b15ca4e2'
					: 'https://help.stepin.ai/terms/eula',
		},
		{
			key: 'registerAgreements.privacy',
			label: t('privacy'),
			viewLink:
				locale === 'ko'
					? 'https://help.stepin.ai/9c97b89c-d0ff-466f-9ee5-8ca5cb3c22c0'
					: 'https://help.stepin.ai/terms/privacy_policy',
		},
		{ key: 'registerAgreements.age14', label: t('age'), viewLink: '' },
		{ key: 'registerAgreements.marketing', label: t('marketing'), viewLink: '' },
	] as const;

	const isRequiredFieldsChecked = () => {
		return (
			getValues('registerAgreements.stepin') &&
			getValues('registerAgreements.privacy') &&
			getValues('registerAgreements.age14')
		);
	};

	const handleAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.checked;
		setIsAllChecked(newValue);
		[
			'registerAgreements.stepin',
			'registerAgreements.privacy',
			'registerAgreements.age14',
			'registerAgreements.marketing',
		].forEach((name: any) => {
			setValue(name, newValue);
		});
	};

	// ========== ✅ Roving Tabindex (전체 동의 + 4개 체크박스 = 5개) ==========
	const { getRovingProps } = useRovingTabindex({
		count: 5, // 전체 동의 1개 + 개별 4개
		onSelect: (index) => {
			if (index === 0) {
				// 전체 동의 토글
				const syntheticEvent = {
					target: { checked: !isAllChecked },
				} as React.ChangeEvent<HTMLInputElement>;
				handleAllChange(syntheticEvent);
			} else {
				// 개별 체크박스 토글
				const key = checkboxItems[index - 1].key;
				setValue(key, !getValues(key));
			}
		},
		direction: 'vertical',
		loop: true,
		enableTabExit: true,
	});

	return (
		<>
			<div className={styles.title}>
				<div className={styles.line2}>
					<span>{t('subtitle')}</span>
					<Link className={styles.link} href={LOGIN_PATHNAME(locale)}>
						{t('loginLink')}
					</Link>
				</div>
			</div>
			<div className={styles.agreement}>
				<div className={styles.checkboxes}>
					<AllAuthCheckbox
						{...getRovingProps(0)}
						checked={isAllChecked}
						frameClass={styles.allFrame}
						handleChange={handleAllChange}
						keyProp="all"
						label={t('all')}
						labelClass={styles.allLabel}
						groupMode={true}
					/>

					{checkboxItems.map(({ key, label, viewLink }, index) => (
						<AuthCheckbox
							{...getRovingProps(index + 1)}
							frameClass={styles.wrapper}
							key={key}
							keyProp={key}
							label={label}
							labelClass={styles.label}
							optional={key === 'registerAgreements.marketing' ? true : false}
							register={register(key, {
								required: key === 'registerAgreements.marketing' ? false : true,
							})}
							required={key === 'registerAgreements.marketing' ? false : true}
							viewLink={viewLink}
							groupMode={true}
						/>
					))}
				</div>

				<div className={styles.submitArea}>
					<SubmitBtn
						isSubmitting={false}
						isValid={isRequiredFieldsChecked()}
						onClick={onNext}
						text={t('next')}
						type="button"
					/>
				</div>
			</div>
		</>
	);
};

export default RegisterAgreements;
